import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { MessagePopupCore } from '@utahdts/dts-react-common';
import {
  completeRedirect,
  getValidToken,
  setCachedToken,
  signIn,
  subscribeAuth,
} from './firebaseAuth';
import { signOutEverywhere } from './logout';
import appStore from '../mobx/appStore';
import menuStore from '../mobx/menuStore';
import webservice from '../webservice/webservice';
import { debugLog } from '../debug/devLogger';

const FirebaseAuthContext = createContext(null);

// Refresh the Firebase ID token before its ~1h expiry.
const TOKEN_REFRESH_MS = 50 * 60 * 1000;

const propTypes = {
  children: PropTypes.node,
};
const defaultProps = {
  children: undefined,
};

/**
 * Push the resolved backend user profile into MobX and rebuild the menu
 * (mirrors ReduxToMobXShim.setUtahHeaderLoggedInUser). In-memory only — no
 * browser storage.
 */
const setLoggedInUser = (userProfile) => {
  appStore.loggedInUser = userProfile;
  // email is the pharmacy-user business key; mirror it from the backend
  // profile when present so role lookups have it directly.
  if (userProfile && userProfile.email) {
    appStore.email = userProfile.email;
  }

  // Derive the user's orgs (by FEIN business key) and role names from the
  // backend profile roles. A role's org relationship is the FEIN (Rule 03), so
  // `role.fein || role.org` is the org business key. We de-dupe, drop roles with
  // no org (e.g. global Administrator/Developer roles), and preserve any org
  // record already known in userOrgs (keeping its name/id) so the company name
  // still resolves via currentOrgRecord.
  if (userProfile && Array.isArray(userProfile.roles)) {
    const feins = [
      ...new Set(
        userProfile.roles
          .map((role) => (role ? role.fein || role.org : null))
          .filter(Boolean),
      ),
    ];
    const knownByFein = new Map(
      (appStore.userOrgs || [])
        .filter((org) => org && org.fein)
        .map((org) => [org.fein, org]),
    );
    appStore.userOrgs = feins.map((fein) => knownByFein.get(fein) || { fein });

    appStore.userRoles = [
      ...new Set(
        userProfile.roles
          .map((role) => (role ? role.role : null))
          .filter(Boolean),
      ),
    ];
  } else {
    appStore.userOrgs = [];
    appStore.userRoles = [];
  }

  debugLog(
    'auth',
    'setLoggedInUser (backend profile)',
    userProfile ? { email: userProfile.email, roles: userProfile.roles } : null,
  );
  menuStore.setupMenu();
};

/**
 * Given a signed-in Firebase user, attach its ID token to MobX as the
 * `oidcUser` (so the existing webservice header logic keeps working) and load
 * the backend user profile. In-memory only — no browser storage.
 */
const syncFirebaseUser = async (user) => {
  debugLog('auth', 'syncFirebaseUser: firebase user', {
    uid: user.uid,
    email: user.email,
  });
  const idToken = await user.getIdToken();
  setCachedToken(idToken);
  debugLog('auth', 'syncFirebaseUser: id token retrieved', {
    hasToken: !!idToken,
  });

  const oidcUser = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    // token fields consumed by graphQLWebservice loadDefaultsCallback
    access_token: idToken,
    id_token: idToken,
  };
  appStore.oidcUser = oidcUser;
  // pharmacy-user business key — kept in memory only.
  appStore.email = user.email;

  try {
    const backendUser = await webservice.user.loggedInUser();
    debugLog(
      'auth',
      'syncFirebaseUser: backend loggedInUser resolved',
      backendUser
        ? { email: backendUser.email, roles: backendUser.roles }
        : null,
    );
    setLoggedInUser(backendUser);
  } catch (error) {
    debugLog('auth', 'syncFirebaseUser: backend loggedInUser FAILED', error);
    MessagePopupCore.addMessage({
      title: 'Authentication Error',
      message: 'There was a problem retrieving your user credentials.',
      subMessage: error?.message || String(error),
    });
  }
};

const clearUser = () => {
  debugLog('auth', 'clearUser (sign-out)');
  setCachedToken(null);
  appStore.signOut();
};

/**
 * Provides Firebase Authentication state to the app. Handles the redirect
 * result on load, tracks the session via onAuthStateChanged, keeps the ID token
 * fresh, and exposes `login` / `logout`.
 */
export const FirebaseAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [ready, setReady] = useState(false);

  // Complete a pending redirect sign-in (no-op when there is none).
  useEffect(() => {
    debugLog('auth', 'completeRedirect: checking for a pending OIDC redirect');
    completeRedirect()
      .then((cred) =>
        debugLog(
          'auth',
          'completeRedirect: result',
          cred ? { uid: cred.user?.uid, email: cred.user?.email } : 'none',
        ),
      )
      .catch((error) => {
        MessagePopupCore.addMessage({
          title: 'Sign-in',
          message: 'Could not complete sign-in. Please try again.',
          subMessage: (error && error.message) || String(error),
        });
      });
  }, []);

  // Debug: snapshot the in-memory session on page load and whenever the tab
  // regains focus/visibility (no-op unless the devLogger is enabled).
  useEffect(() => {
    appStore._debugSnapshot?.('page-load');
    const onFocus = () => appStore._debugSnapshot?.('tab-focus');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        appStore._debugSnapshot?.('tab-visible');
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Track the session.
  useEffect(() => {
    const unsubscribe = subscribeAuth(async (user) => {
      debugLog(
        'auth',
        'onAuthStateChanged callback',
        user ? { uid: user.uid, email: user.email } : 'no user (signed out)',
      );
      if (user) {
        try {
          await syncFirebaseUser(user);
          setCurrentUser(user);
        } catch {
          setCurrentUser(null);
          clearUser();
        }
      } else {
        setCurrentUser(null);
        clearUser();
      }
      setReady(true);
      appStore._debugSnapshot?.('auth-callback');
    });
    return unsubscribe;
  }, []);

  // Keep the ID token fresh.
  useEffect(() => {
    if (!currentUser) {
      return undefined;
    }
    const interval = setInterval(async () => {
      try {
        const token = await getValidToken(true);
        if (token && appStore.oidcUser) {
          appStore.oidcUser = {
            ...appStore.oidcUser,
            access_token: token,
            id_token: token,
          };
          debugLog('auth', 'ID token refreshed');
        }
      } catch {
        /* transient refresh failure — next interval retries */
      }
    }, TOKEN_REFRESH_MS);
    return () => clearInterval(interval);
  }, [currentUser]);

  const login = useCallback(() => {
    debugLog('auth', 'login: starting id.utah.gov OIDC redirect');
    return signIn();
  }, []);
  const logout = useCallback(() => {
    debugLog('auth', 'logout: sign out Firebase locally, then IdP endSession');
    // signOutEverywhere signs the app out of Firebase and clears the in-memory
    // session up-front (guaranteed), then redirects to AM's endSession to end the
    // id.utah.gov SSO session — the same shared logout the main menu and Utah ID
    // header use.
    return signOutEverywhere();
  }, []);

  const value = useMemo(
    () => ({ currentUser, ready, login, logout }),
    [currentUser, ready, login, logout],
  );

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
};

FirebaseAuthProvider.propTypes = propTypes;
FirebaseAuthProvider.defaultProps = defaultProps;

export const useFirebaseAuth = () => {
  const value = useContext(FirebaseAuthContext);
  if (value === null) {
    throw new Error(
      'useFirebaseAuth must be used within a <FirebaseAuthProvider />',
    );
  }
  return value;
};

export default FirebaseAuthProvider;
